import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserRound, KeyRound, Save, Eye, EyeOff } from "lucide-react";

const ROLE = { admin: "Administrateur", seller: "Cédant", buyer: "Acheteur / repreneur" };

const Field = ({ id, label, value, onChange, ...props }) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
    <Input id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1.5 h-11 border-gray-300" {...props} />
  </div>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-gray-200 p-6 sm:p-8 space-y-5">
    <h2 className="font-display text-lg font-semibold text-[#1B2A4A] flex items-center gap-2"><Icon className="w-5 h-5 text-[#E0C173]" strokeWidth={1.5} /> {title}</h2>
    {children}
  </section>
);

const ProfileForm = ({ user, setUser }) => {
  const [f, setF] = useState({ name: user.name || "", email: user.email, phone: user.phone || "", company: user.company || "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const emailChanged = f.email.trim().toLowerCase() !== user.email;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.put("/auth/profile", f); setUser({ ...user, ...data }); setF((x) => ({ ...x, current_password: "" })); toast.success("Coordonnées mises à jour"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="profile-name" label="Nom et prénom" value={f.name} onChange={set("name")} required minLength={2} />
        <Field id="profile-phone" label="Téléphone" type="tel" value={f.phone} onChange={set("phone")} />
      </div>
      <Field id="profile-email" label="Email (identifiant de connexion)" type="email" value={f.email} onChange={set("email")} required disabled={user.auth_provider === "google"} />
      {emailChanged && <Field id="profile-current-password" label="Mot de passe actuel (requis pour changer d'email)" type="password" value={f.current_password} onChange={set("current_password")} required autoComplete="current-password" />}
      <Field id="profile-company" label="Société" value={f.company} onChange={set("company")} />
      <button data-testid="profile-save" disabled={busy} className="inline-flex items-center gap-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white px-6 py-2.5 text-sm disabled:opacity-50"><Save className="w-4 h-4" /> {busy ? "Enregistrement…" : "Enregistrer mes coordonnées"}</button>
    </form>
  );
};

const PasswordForm = ({ user, setUser }) => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Les deux mots de passe ne correspondent pas.");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); setUser({ ...user, has_password: true }); setF({ current_password: "", new_password: "", confirm: "" }); toast.success("Mot de passe modifié. Vos autres appareils ont été déconnectés."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  const type = show ? "text" : "password";
  return (
    <form onSubmit={save} className="space-y-4">
      {user.has_password && <Field id="password-current" label="Mot de passe actuel" type={type} value={f.current_password} onChange={set("current_password")} required autoComplete="current-password" />}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="password-new" label="Nouveau mot de passe (8 caractères min.)" type={type} value={f.new_password} onChange={set("new_password")} required minLength={8} autoComplete="new-password" />
        <Field id="password-confirm" label="Confirmer le nouveau mot de passe" type={type} value={f.confirm} onChange={set("confirm")} required minLength={8} autoComplete="new-password" />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <button data-testid="password-save" disabled={busy} className="inline-flex items-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6 py-2.5 text-sm disabled:opacity-50"><KeyRound className="w-4 h-4" /> {busy ? "Modification…" : user.has_password ? "Changer mon mot de passe" : "Définir mon mot de passe"}</button>
        <button type="button" data-testid="password-toggle" onClick={() => setShow(!show)} className="inline-flex items-center gap-1.5 text-sm text-[#475569]">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {show ? "Masquer" : "Afficher"}</button>
      </div>
    </form>
  );
};

export default function Account() {
  const { user, setUser, checkAuth } = useAuth();
  useEffect(() => { checkAuth(); }, [checkAuth]);
  return (
    <div className="bg-[#FDFDFC]">
      <Header />
      <section className="bg-[#1B2A4A] py-14">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">{ROLE[user.role]}</span>
          <h1 data-testid="account-title" className="font-display text-4xl sm:text-5xl font-semibold text-white mt-3">Mon compte</h1>
        </div>
      </section>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 space-y-8">
        <Card icon={UserRound} title="Mes coordonnées" testid="account-profile"><ProfileForm key={user.email} user={user} setUser={setUser} /></Card>
        <Card icon={KeyRound} title="Mot de passe" testid="account-password"><PasswordForm user={user} setUser={setUser} /></Card>
      </div>
      <Footer />
    </div>
  );
}
